import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  UserPreferenceKey,
  type UserPreferencesResponse,
} from '@kirin/api-client/contracts/user-preferences';
import type { ThemePreferenceType } from '@/styles/types';
import { usePreferencesQuery } from '@/hooks/preferences/usePreferencesQuery';
import { useThemeSync, type ThemeSync } from '@/hooks/preferences/useThemeSync';
import { queryKeys } from '@/hooks/api/queryKeys';
import { mergePreference } from '@/lib/preferences/mergePreference';
import { getThemeFromPreferences } from '@/lib/preferences/selectPreference';

export interface ThemePreferencesSync {
  themePreference: ThemePreferenceType;
  sync: ThemeSync;
  commitToCache: (next: ThemePreferenceType) => void;
}

export function useThemePreferencesSync(
  // First-paint seed used until the boot query resolves; cache shadows it via `??`.
  fallbackTheme: ThemePreferenceType
): ThemePreferencesSync {
  const query = usePreferencesQuery();
  const queryClient = useQueryClient();

  const cachedTheme = query.data
    ? getThemeFromPreferences(query.data)
    : undefined;
  const themePreference = cachedTheme ?? fallbackTheme;

  const sync = useThemeSync(themePreference);

  const commitToCache = useCallback(
    (next: ThemePreferenceType): void => {
      queryClient.setQueryData<UserPreferencesResponse>(
        queryKeys.preferences.all,
        old =>
          mergePreference(old, { key: UserPreferenceKey.Theme, value: next })
      );
    },
    [queryClient]
  );

  return useMemo(
    () => ({ themePreference, sync, commitToCache }),
    [themePreference, sync, commitToCache]
  );
}
